<template>
    <info-widget :collapsible="true" :is-expanded="expanded" @toggle_expand="expanded = !expanded">
        <template v-slot:title>Labor Allocation</template>
        <template v-slot:headerbutton>
            <span v-if="expanded && ledgerTotals" class="labor-expand-all mr-2" role="button" @click.stop.prevent="toggleAll">{{ allExpanded ? 'Collapse All' : 'Expand All' }}</span>
        </template>
        <slot>
            <div class="p-2">
                <div v-if="ledgerTotalsStatus === 'computing'" class="p-3 text-center text-muted">
                    <i class="fa fa-spinner fa-spin mr-2"></i>
                </div>
                <div v-else-if="ledgerTotalsStatus === 'failed'" class="p-3 text-center text-muted">
                    Unable to compute labor allocation for this run.
                </div>
                <div v-else-if="ledgerTotals">
                    <labor-allocation-level :level="ledgerTotals.run" path-prefix="run" :is-expanded="isExpanded" :toggle="toggle" />

                    <div class="p-1 pr-2 pt-2">
                        <div v-for="location in ledgerTotals.locations" :key="location.work_location.id" class="pl-1">
                            <info-widget :collapsible="true" :is-expanded="isLocationExpanded(location.work_location.id)" @toggle_expand="toggleLocation(location.work_location.id)">
                                <template v-slot:title><span class="text-muted">Location: </span>{{ location.work_location.name }}</template>
                                <template v-slot:headerbutton>
                                    <span v-if="isLocationExpanded(location.work_location.id)" class="labor-expand-all mr-2" role="button" @click.stop.prevent="toggleAllForLocation(location)">{{ allExpandedForLocation(location) ? 'Collapse All' : 'Expand All' }}</span>
                                </template>
                                <labor-allocation-level :level="location" :path-prefix="locationPrefix(location)" :is-expanded="isExpanded" :toggle="toggle" />
                            </info-widget>
                        </div>
                    </div>
                </div>
            </div>
        </slot>
    </info-widget>
</template>

<script>
import InfoWidget from '@/components/InfoWidget'
import LaborAllocationLevel from './LaborAllocationLevel'

export default {
    components: {InfoWidget: InfoWidget, LaborAllocationLevel: LaborAllocationLevel},
    props: ['ledgerTotals', 'ledgerTotalsStatus'],
    data() {
        return {
            expanded: true,
            expandedLocations: {},
            // Expansion state for every collapsible row (categories, items, positions) across
            // the run level and all locations, keyed by full path. Lifted here so the
            // Expand All / Collapse All control in each location header can drive the
            // controlled LaborAllocationLevel / LaborAllocationRows children.
            expandedKeys: {},
        }
    },
    computed: {
        // Every collapsible row path across the run level and all locations.
        allExpandablePaths() {
            if (!this.ledgerTotals) {
                return []
            }
            let paths = this.collectExpandablePaths(this.ledgerTotals.run, 'run')
            for (const location of this.ledgerTotals.locations) {
                paths = paths.concat(this.collectExpandablePaths(location, this.locationPrefix(location)))
            }
            return paths
        },
        allLocationIds() {
            if (!this.ledgerTotals) {
                return []
            }
            return this.ledgerTotals.locations.map(location => location.work_location.id)
        },
        // Everything is expanded: all location widgets open and every row expanded.
        allExpanded() {
            const paths = this.allExpandablePaths
            const locationIds = this.allLocationIds
            if (paths.length === 0 && locationIds.length === 0) {
                return false
            }
            const rowsExpanded = paths.every(path => this.expandedKeys[path])
            const locationsExpanded = locationIds.every(id => this.expandedLocations[id])
            return rowsExpanded && locationsExpanded
        },
    },
    methods: {
        toggleAll() {
            const expand = !this.allExpanded
            const nextKeys = Object.assign({}, this.expandedKeys)
            this.allExpandablePaths.forEach(path => { nextKeys[path] = expand })
            this.expandedKeys = nextKeys
            const nextLocations = Object.assign({}, this.expandedLocations)
            this.allLocationIds.forEach(id => { nextLocations[id] = expand })
            this.expandedLocations = nextLocations
        },
        isLocationExpanded(locationId) {
            return !!this.expandedLocations[locationId]
        },
        toggleLocation(locationId) {
            this.expandedLocations[locationId] = !this.expandedLocations[locationId]
        },
        isExpanded(path) {
            return !!this.expandedKeys[path]
        },
        toggle(path) {
            this.expandedKeys[path] = !this.expandedKeys[path]
        },
        locationPrefix(location) {
            return 'loc-' + location.work_location.id
        },
        // Every collapsible row's full path within a level, so Expand All knows what to toggle.
        collectExpandablePaths(level, prefix) {
            const paths = []
            const walk = function (nodes, parentPath) {
                for (const node of nodes) {
                    if (node.rows && node.rows.length) {
                        const path = parentPath + '/' + node.key
                        paths.push(path)
                        walk(node.rows, path)
                    }
                }
            }
            walk((level && level.categories) || [], prefix)
            return paths
        },
        allExpandedForLocation(location) {
            const paths = this.collectExpandablePaths(location, this.locationPrefix(location))
            return paths.length > 0 && paths.every(path => this.expandedKeys[path])
        },
        toggleAllForLocation(location) {
            const paths = this.collectExpandablePaths(location, this.locationPrefix(location))
            const expand = !this.allExpandedForLocation(location)
            const next = Object.assign({}, this.expandedKeys)
            paths.forEach(path => { next[path] = expand })
            this.expandedKeys = next
        },
    },
}
</script>

<style scoped lang="scss">
.labor-expand-all {
    color: #6c757d;
    cursor: pointer;
    white-space: nowrap;
    font-size: 0.85rem;
    font-weight: 400;

    &:hover {
        color: #495057;
        text-decoration: underline;
    }
}
</style>
